import {
  doc,
  getDoc,
  collection,
  getDocs,
  getCountFromServer,
  DocumentSnapshot,
  CollectionReference
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { RepositoryError } from '../../domain/errors';
import { ILoggerService } from '../../domain/services/LoggerService';
import { UserContext } from '../../domain/value-objects/UserContext';
import { FirestoreQueryBuilder, QueryOptions, FilterCondition, DateRangeFilter } from '../services/FirestoreQueryBuilder';

export type { FilterCondition, DateRangeFilter };

export interface BaseListResponse<T> {
  items: T[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

export interface PaginationResult<T> {
  items: T[];
  lastDoc?: DocumentSnapshot;
}

export abstract class BaseFirebaseRepository<T> {
  protected readonly collection: CollectionReference;
  protected readonly logger: ILoggerService;

  constructor(
    protected readonly collectionName: string,
    logger: ILoggerService
  ) {
    this.collection = collection(db, collectionName);
    this.logger = logger;
  }

  protected abstract convertFirestoreToEntity(id: string, data: any): T;
  protected abstract getDefaultSortField(): string;
  protected abstract mapSortField(sortBy: string): string;

  protected buildUserFilters(
    userContext: UserContext | undefined,
    requestedOrgId?: string
  ): FilterCondition[] {
    if (!userContext) return [];

    const filters: FilterCondition[] = [];

    switch (userContext.role) {
      case 'admin':
        if (requestedOrgId) {
          filters.push({ field: 'organizationId', operator: '==', value: requestedOrgId });
        }
        break;

      case 'user':
      case 'organization_user':
        filters.push({ field: 'organizationId', operator: '==', value: userContext.organizationId });
        break;

      case 'locations_user':
        filters.push({ field: 'organizationId', operator: '==', value: userContext.organizationId });
        if (userContext.locationIds && userContext.locationIds.length > 0) {
          filters.push(...this.applyLocationScopeFilters(userContext.locationIds));
        }
        break;
    }

    return filters;
  }

  protected applyLocationScopeFilters(locationIds: string[]): FilterCondition[] {
    // or-equals generates or(where('locationId','==',id1), where('locationId','==',id2), ...)
    // Firestore Security Rules evaluate each == branch independently, which is the only
    // form the rules engine can prove satisfies `locationId in getUserData().locationIds`.
    // The `in` operator is not reliably proven by the rules engine for locations_user.
    return [{ field: 'locationId', operator: 'or-equals', value: locationIds }];
  }

  private mergeUserAndAdditionalFilters(
    userFilters: FilterCondition[],
    additionalFilters: FilterCondition[]
  ): FilterCondition[] {
    // Strip the location scope filter (or-equals / in) when the caller has already
    // pinned the query to a specific location with locationId ==. The == constraint
    // stays rules-enforced: canAccessByLocation rejects any X outside locationIds.
    const hasLocationEquality = additionalFilters.some(
      f => f.field === 'locationId' && f.operator === '=='
    );
    if (!hasLocationEquality) return userFilters;
    return userFilters.filter(
      f => !(f.field === 'locationId' && (f.operator === 'or-equals' || f.operator === 'in'))
    );
  }

  async findById(id: string, userContext?: UserContext): Promise<T | null> {
    const startTime = Date.now();
    this.logger.logRepositoryRequest(this.collectionName, 'findById', { id, userContext });
    
    try {
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);

      let result: T | null = null;
      if (docSnap.exists()) {
        result = this.convertFirestoreToEntity(docSnap.id, docSnap.data());
      }

      const duration = Date.now() - startTime;
      this.logger.logRepositoryResponse(this.collectionName, 'findById', duration, { 
        found: result !== null,
        id: result?.constructor.name || null
      });

      return result;
    } catch (error) {
      const duration = Date.now() - startTime;
      this.logger.logRepositoryError(this.collectionName, 'findById', duration, error);
      this.logger.error(`${this.collectionName} findById failed`, error);
      throw RepositoryError.queryFailed();
    }
  }

  protected async executeQuery(
    userContext?: UserContext,
    options: QueryOptions = {},
    additionalFilters: FilterCondition[] = [],
    dateRanges: DateRangeFilter[] = [],
    requestedOrgId?: string
  ): Promise<BaseListResponse<T>> {
    const startTime = Date.now();
    this.logger.logRepositoryRequest(this.collectionName, 'executeQuery', {
      userContext,
      options,
      additionalFilters,
      dateRanges: dateRanges.length,
      requestedOrgId
    });
    
    try {
      const {
        page = 1,
        pageSize = 10,
        search
      } = options;

      // Get paginated results and total count in parallel
      const [paginationResult, totalCount] = await Promise.all([
        this.getPaginatedResults(userContext, options, additionalFilters, dateRanges, requestedOrgId),
        this.getTotalCount(userContext, additionalFilters, dateRanges, requestedOrgId)
      ]);

      let items = paginationResult.items;

      // Apply client-side search filtering if search term is provided
      if (search && search.trim()) {
        const originalCount = items.length;
        items = this.applySearchFilter(items, search);
        this.logger.debug(`${this.collectionName} search filter applied`, { 
          searchTerm: search, 
          originalCount, 
          filteredCount: items.length 
        });
      }

      const totalPages = Math.ceil(totalCount / pageSize);

      const result = {
        items,
        totalCount,
        totalPages,
        currentPage: page,
        pageSize
      };

      const duration = Date.now() - startTime;
      this.logger.logRepositoryResponse(this.collectionName, 'executeQuery', duration, {
        itemsReturned: result.items.length,
        totalCount: result.totalCount,
        totalPages: result.totalPages,
        currentPage: result.currentPage,
        pageSize: result.pageSize
      });

      return result;
    } catch (error) {
      const duration = Date.now() - startTime;
      this.logger.logRepositoryError(this.collectionName, 'executeQuery', duration, error);
      this.logger.error(`${this.collectionName} executeQuery failed`, error);
      throw RepositoryError.queryFailed();
    }
  }

  private async getPaginatedResults(
    userContext?: UserContext,
    options: QueryOptions = {},
    additionalFilters: FilterCondition[] = [],
    dateRanges: DateRangeFilter[] = [],
    requestedOrgId?: string
  ): Promise<PaginationResult<T>> {
    const {
      page = 1,
      pageSize = 10,
      sortBy = this.getDefaultSortField(),
      sortOrder = 'asc'
    } = options;

    // Build user filters
    const rawUserFilters = this.buildUserFilters(userContext, requestedOrgId);
    const userFilters = this.mergeUserAndAdditionalFilters(rawUserFilters, additionalFilters);

    this.logger.logRepositoryRequest(this.collectionName, 'getPaginatedResults:firestoreFilters', {
      effectiveFilters: [...userFilters, ...additionalFilters]
    });

    let queryBuilder = FirestoreQueryBuilder.create(this.collection)
      .withFilters([...userFilters, ...additionalFilters]);

    // Add date range filters
    dateRanges.forEach(dateRange => {
      queryBuilder = queryBuilder.withDateRange(dateRange);
    });

    // Add sorting and pagination
    queryBuilder = queryBuilder.withSorting(sortBy, sortOrder, this.mapSortField.bind(this));

    // Handle pagination
    let lastDoc: DocumentSnapshot | undefined;
    if (page > 1) {
      const skipCount = (page - 1) * pageSize;
      
      // Get documents to skip
      const tempQuery = FirestoreQueryBuilder.create(this.collection)
        .withFilters([...userFilters, ...additionalFilters]);
      
      dateRanges.forEach(dateRange => {
        tempQuery.withDateRange(dateRange);
      });
      
      const tempQueryBuilt = tempQuery
        .withSorting(sortBy, sortOrder, this.mapSortField.bind(this))
        .withPagination({ pageSize: skipCount })
        .build();

      const tempSnapshot = await getDocs(tempQueryBuilt);
      
      if (tempSnapshot.docs.length > 0) {
        lastDoc = tempSnapshot.docs[tempSnapshot.docs.length - 1];
      }
    }

    const finalQuery = queryBuilder
      .withPagination({ pageSize, lastDoc })
      .build();

    const querySnapshot = await getDocs(finalQuery);

    const items = querySnapshot.docs.map(doc => 
      this.convertFirestoreToEntity(doc.id, doc.data())
    );

    return {
      items,
      lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1]
    };
  }

  private async getTotalCount(
    userContext?: UserContext,
    additionalFilters: FilterCondition[] = [],
    dateRanges: DateRangeFilter[] = [],
    requestedOrgId?: string
  ): Promise<number> {
    const startTime = Date.now();
    
    try {
      // Build user filters
      const rawUserFilters = this.buildUserFilters(userContext, requestedOrgId);
      const userFilters = this.mergeUserAndAdditionalFilters(rawUserFilters, additionalFilters);

      this.logger.logRepositoryRequest(this.collectionName, 'getTotalCount:firestoreFilters', {
        effectiveFilters: [...userFilters, ...additionalFilters]
      });

      let queryBuilder = FirestoreQueryBuilder.create(this.collection)
        .withFilters([...userFilters, ...additionalFilters]);

      // Add date range filters
      dateRanges.forEach(dateRange => {
        queryBuilder = queryBuilder.withDateRange(dateRange);
      });

      const countQuery = queryBuilder.buildCount();
      const snapshot = await getDocs(countQuery);
      const count = snapshot.size;

      const duration = Date.now() - startTime;
      this.logger.debug(`${this.collectionName} getTotalCount completed (${duration}ms)`, { count });

      return count;
    } catch (error) {
      const duration = Date.now() - startTime;
      this.logger.error(`${this.collectionName} getTotalCount failed (${duration}ms)`, error);
      throw RepositoryError.queryFailed();
    }
  }

  protected async countWithFilters(
    userContext?: UserContext,
    additionalFilters: FilterCondition[] = [],
    dateRanges: DateRangeFilter[] = [],
    requestedOrgId?: string
  ): Promise<number> {
    const startTime = Date.now();
    this.logger.logRepositoryRequest(this.collectionName, 'countWithFilters', { 
      userContext, 
      additionalFilters: additionalFilters.length,
      dateRanges: dateRanges.length,
      requestedOrgId
    });
    
    try {
      const rawUserFilters = this.buildUserFilters(userContext, requestedOrgId);
      const userFilters = this.mergeUserAndAdditionalFilters(rawUserFilters, additionalFilters);

      let queryBuilder = FirestoreQueryBuilder.create(this.collection)
        .withFilters([...userFilters, ...additionalFilters]);

      // Add date range filters
      dateRanges.forEach(dateRange => {
        queryBuilder = queryBuilder.withDateRange(dateRange);
      });

      const countQuery = queryBuilder.buildCount();
      const snapshot = await getCountFromServer(countQuery);
      const count = snapshot.data().count;

      const duration = Date.now() - startTime;
      this.logger.logRepositoryResponse(this.collectionName, 'countWithFilters', duration, { count });

      return count;
    } catch (error) {
      const duration = Date.now() - startTime;
      this.logger.logRepositoryError(this.collectionName, 'countWithFilters', duration, error);
      this.logger.error(`${this.collectionName} countWithFilters failed`, error);
      throw RepositoryError.queryFailed();
    }
  }

  protected convertFirestoreDate(value: any): Date {
    if (!value) {
      return new Date();
    }
    
    if (value instanceof Date) {
      return value;
    }
    
    if (value && typeof value.toDate === 'function') {
      return value.toDate();
    }
    
    if (typeof value === 'number') {
      return new Date(value);
    }
    
    if (value && typeof value.seconds === 'number') {
      return new Date(value.seconds * 1000);
    }
    
    return new Date();
  }

  protected convertFirestoreDateNullable(value: any): Date | null {
    if (!value) {
      return null;
    }
    
    if (value instanceof Date) {
      return value;
    }
    
    if (value && typeof value.toDate === 'function') {
      return value.toDate();
    }
    
    if (typeof value === 'number') {
      return new Date(value);
    }
    
    if (value && typeof value.seconds === 'number') {
      return new Date(value.seconds * 1000);
    }
    
    return null;
  }

  protected abstract applySearchFilter(items: T[], search: string): T[];
}